import * as React from 'react';
import { cn } from '@/lib/utils';
import { NavRail } from './NavRail';
import { TopBar } from './TopBar';
import { MobileDrawer } from './MobileDrawer';
import { SidebarProvider } from '@/components/ui/sidebar';
import { FeedbackModal } from '@/components/FeedbackModal';
import { SystemStatusBanner } from '@/components/SystemStatusBanner';
import { MaintenanceOverlay } from '@/components/MaintenanceOverlay';
import { NewVersionBanner } from '@/components/NewVersionBanner';
import { usePermissionsSync } from '@/hooks/use-permissions-sync';
import { isStagingEnvironment } from '@/lib/staging-environment';

// Sprint 38 — Contractual Precision app shell.
//
// Composition (viewport-locked so a long <main> does not stretch the rail):
//   ┌────────────────────────────────────────────┐
//   │ TopBar (h-14, brand + profile)             │
//   ├──────────┬─────────────────────────────────┤
//   │ NavRail  │  <main> (independent scroll)    │
//   │ sticky   │                                 │
//   │ h-svh    │                                 │
//   └──────────┴─────────────────────────────────┘
//
// Responsive (DESIGN.md §9):
//   < 640px (phone)  : NavRail hidden, hamburger opens MobileDrawer.
//   640–1024 (tablet): NavRail in collapsed (icon-only) mode, ~56px wide.
//   > 1024 (desktop) : NavRail full at 240px.
//
// Brand block (the LarkPM logo + tagline) renders inside both the
// desktop NavRail header and the MobileDrawer for consistency.

interface AppShellProps {
  children: React.ReactNode;
  /** Optional handler invoked when user clicks Feedback nav entry.
   *  Wire this to your <FeedbackModal> open state when Phase 4.5 lands.
   *  Until then, leaving it undefined is fine — the button is a no-op. */
  onOpenFeedback?: () => void;
  /** When true, the nav rail collapses to icon-only on tablet sizes.
   *  Defaults to true (auto-collapse via media query).  Pass false to
   *  always show full labels. */
  responsive?: boolean;
}

function BrandBlock() {
  return (
    <div className="flex items-center gap-3">
      <div className="h-9 w-9 shrink-0 overflow-hidden rounded-none border border-border bg-background">
        <img src="/SideLogo.png" alt="" className="h-full w-full object-cover" />
      </div>
      <div>
        <p className="font-newsreader text-lg font-semibold leading-none text-foreground">
          LarkPM
        </p>
        <p className="mt-0.5 text-[10px] font-bold uppercase tracking-[0.12em] text-foreground-soft">
          {isStagingEnvironment() ? 'Staging' : 'Project Management'}
        </p>
      </div>
    </div>
  );
}

const NAV_COLLAPSED_KEY = 'larkpm:nav-collapsed';

export function AppShell({ children, onOpenFeedback, responsive = true }: AppShellProps) {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  // Sprint 44 — keep stored permissions in step with the server so grants made
  // mid-session (e.g. perm-lead-redact-ip) surface without a re-login. The
  // returned version bumps only on an access-set change; pass it into NavRail
  // so useMemo re-filters the tree against fresh localStorage permissions.
  const accessVersion = usePermissionsSync();
  // Sprint 41 — User-controlled left-rail collapse on desktop. Persists
  // across screens via localStorage so the user's preference survives
  // navigation + reload. Tablet auto-collapse (md..lg) is unchanged.
  const [navCollapsed, setNavCollapsed] = React.useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.localStorage.getItem(NAV_COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });
  React.useEffect(() => {
    try {
      window.localStorage.setItem(NAV_COLLAPSED_KEY, navCollapsed ? '1' : '0');
    } catch {
      /* noop */
    }
  }, [navCollapsed]);
  const toggleNav = React.useCallback(() => setNavCollapsed((c) => !c), []);
  // Sprint 38 — Feedback modal state lives here so every page that
  // renders inside MainLayout/AppShell automatically gets the modal.
  // The optional onOpenFeedback prop callers can still pass to override
  // behavior (e.g. analytics tracking) — when present, the AppShell
  // delegates to it INSTEAD of opening the bundled modal.
  const [feedbackOpen, setFeedbackOpen] = React.useState(false);
  const handleOpenFeedback = onOpenFeedback ?? (() => setFeedbackOpen(true));

  // Wrap in SidebarProvider as a backward-compat shim — pages that still
  // call useSidebar() (e.g. ReportsPage) need this context to mount even
  // though we no longer render a shadcn <Sidebar>. The provider supplies
  // the open/closed/isMobile context they read; visually it's a no-op.
  return (
    <SidebarProvider className="h-svh max-h-svh overflow-hidden">
    <div
      data-testid="app-shell"
      className="flex h-full min-h-0 w-full overflow-hidden bg-background text-foreground"
    >
      {/* Desktop rail — viewport-height + sticky so a long contract (or any
          tall page) cannot stretch the nav and hide Settings below the fold. */}
      <aside
        data-testid="app-shell-nav"
        className={cn(
          'hidden lg:flex',
          'sticky top-0 h-svh max-h-svh self-start',
          'shrink-0 transition-[width] duration-200 ease-in-out',
          navCollapsed ? 'w-14' : (responsive ? 'lg:w-60 xl:w-64' : 'w-64'),
        )}
      >
        <NavRail
          brand={navCollapsed ? null : <BrandBlock />}
          collapsed={navCollapsed}
          onToggleCollapsed={toggleNav}
          onOpenFeedback={handleOpenFeedback}
          accessVersion={accessVersion}
        />
      </aside>

      {/* Tablet collapsed icon rail — shown on md..lg only */}
      {responsive && (
        <aside
          data-testid="app-shell-nav-tablet"
          className="hidden md:flex lg:hidden sticky top-0 h-svh max-h-svh self-start shrink-0 w-14"
        >
          <NavRail brand={null} collapsed onOpenFeedback={handleOpenFeedback} accessVersion={accessVersion} />
        </aside>
      )}

      {/* Phone drawer */}
      <MobileDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onOpenFeedback={handleOpenFeedback}
        brand={<BrandBlock />}
        accessVersion={accessVersion}
      />

      {/* Sprint 38 — Feedback modal. Opens when NavRail's Feedback button
          is clicked, unless the caller passed a custom onOpenFeedback. */}
      {!onOpenFeedback && (
        <FeedbackModal open={feedbackOpen} onOpenChange={setFeedbackOpen} />
      )}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
        <MaintenanceOverlay />
        <NewVersionBanner />
        <SystemStatusBanner />
        <TopBar onOpenMobileDrawer={() => setDrawerOpen(true)} />
        <main
          data-testid="app-shell-main"
          className="min-h-0 flex-1 overflow-auto overscroll-contain px-4 py-6 sm:px-6 lg:px-10 lg:py-8"
        >
          <div className="mx-auto w-full min-w-0 max-w-container">{children}</div>
        </main>
      </div>
    </div>
    </SidebarProvider>
  );
}
